import Head from 'next/head';

import clsxm from '@/lib/clsxm';

import { tone, useCopyGap } from '@/components/agents/primitives';
import TeamsLogos from '@/components/b2b/TeamsLogos';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';

import Capabilities from './Capabilities';
import Explore from './Explore';
import FinalCta from './FinalCta';
import HowItWorks from './HowItWorks';
import {
  type MusicPageCopy,
  MusicPageCopyProvider,
  MusicPlayerProvider,
  SongIdeaProvider,
  useMusicT,
} from './kit';
import { MUSIC_HOME_PATH } from './links';
import MusicFaq from './MusicFaq';
import MusicHero from './MusicHero';
import PageDetails from './PageDetails';
import Plans from './Plans';
import SoundWall from './SoundWall';
import TwoWays from './TwoWays';
import type { MusicLandingPage } from './types';

export type MusicLandingProps = {
  page: MusicLandingPage;
  /** The page's own strings in this locale (empty in English). */
  copy: MusicPageCopy;
};

/**
 * Every music landing page: the song box first (people arrive here to make a
 * song), then real songs to listen to, how it works, this page's details,
 * what you get, plans, other pages and questions. Pages differ by `page`.
 */
export default function MusicLanding({ page, copy }: MusicLandingProps) {
  return (
    <MusicPageCopyProvider copy={copy}>
      <MusicPlayerProvider pagePath={page.path}>
        <SongIdeaProvider instrumental={Boolean(page.instrumental)}>
          <MusicLandingBody page={page} />
        </SongIdeaProvider>
      </MusicPlayerProvider>
    </MusicPageCopyProvider>
  );
}

function MusicLandingBody({ page }: { page: MusicLandingPage }) {
  const { t, lang } = useMusicT();
  const gap = useCopyGap();
  const title = page.titleTail
    ? `${t(page.title)}${gap}${t(page.titleTail)}`
    : t(page.title);

  const breadcrumbs =
    page.id === 'main'
      ? null
      : {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: t('AI Music Generator'),
              item: absoluteUrl(MUSIC_HOME_PATH, lang),
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: t(page.name),
              item: absoluteUrl(page.path, lang),
            },
          ],
        };

  return (
    // `relative` makes <main> the containing block of the footer's sr-only
    // heading, so overflow-x-clip catches the 1px it pokes out in RTL.
    <main
      className={clsxm('relative overflow-x-clip', tone.page)}
      dir={lang === 'ar' ? 'rtl' : undefined}
    >
      <Seo
        templateTitle={t(page.seoTitle)}
        description={t(page.seoDescription)}
        image={`https://easy-peasy.ai/api/og-template?title=${encodeURIComponent(
          title,
        )}`}
      />
      {breadcrumbs && (
        <Head>
          <script
            type='application/ld+json'
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(breadcrumbs).replace(/</g, '\\u003c'),
            }}
          />
        </Head>
      )}
      <MusicHero page={page} />
      <TeamsLogos
        caption={t('Used by people at companies and universities like')}
      />
      <SoundWall page={page} />
      <HowItWorks />
      <PageDetails page={page} />
      <TwoWays />
      <Capabilities />
      <Plans />
      <Explore currentId={page.id} />
      <MusicFaq page={page} />
      <FinalCta page={page} />
      <Footer />
    </main>
  );
}

function absoluteUrl(path: string, lang: string) {
  return `https://easy-peasy.ai${lang === 'en' ? '' : `/${lang}`}${path}`;
}
